(() => {
  if (window.WiperProSearchByCar?.initialise) {
    window.WiperProSearchByCar.initialise(document);
    return;
  }

  let vehicleDataPromise;

  const hasChooseCarParam = (search = window.location.search) => {
    return new URLSearchParams(search).has('choose-car');
  };

  const isChooseCarLink = (rawHref) => {
    try {
      const url = new URL(rawHref, window.location.origin);
      return url.origin === window.location.origin
        && url.pathname === '/'
        && url.searchParams.has('choose-car');
    } catch (error) {
      return false;
    }
  };

  class SearchByCarModal {
    constructor(root) {
      this.root = root;
      this.dataset = root.dataset;
      this.dialog = root.querySelector('[data-search-by-car-dialog]');
      this.closeButton = root.querySelector('[data-search-by-car-close]');
      this.makeSelect = root.querySelector('[data-search-by-car-make]');
      this.modelSelect = root.querySelector('[data-search-by-car-model]');
      this.submitButton = root.querySelector('[data-search-by-car-submit]');
      this.status = root.querySelector('[data-search-by-car-status]');
      this.error = root.querySelector('[data-search-by-car-error]');
      this.vehicleData = [];
      this.lastTrigger = null;

      if (!this.dialog || !this.closeButton || !this.makeSelect || !this.modelSelect) return;
      this.bindEvents();

      if (window.location.pathname === '/' && hasChooseCarParam()) {
        requestAnimationFrame(() => this.open());
      }
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }

        const link = event.target.closest('a[href]');
        if (!link) return;

        const isExplicitTrigger = link.matches(
          '[data-search-by-car-trigger], #HeaderMenu-search-by-car, #HeaderDrawer-search-by-car, #HeaderMenu-find-my-car, #HeaderDrawer-find-my-car'
        );
        if (!isExplicitTrigger && !isChooseCarLink(link.getAttribute('href'))) return;

        event.preventDefault();
        this.open(link);
      }, true);

      this.closeButton.addEventListener('click', () => this.close());
      this.makeSelect.addEventListener('change', () => this.updateModels());
      this.modelSelect.addEventListener('change', () => this.updateSubmitState());
      this.submitButton.addEventListener('click', () => this.submit());
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
    }

    async open(trigger = null) {
      this.lastTrigger = trigger;
      this.error.hidden = true;

      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }

      document.documentElement.classList.add('search-by-car-modal-open');

      if (this.vehicleData.length > 0) {
        requestAnimationFrame(() => this.makeSelect.focus({ preventScroll: true }));
        return;
      }

      this.setLoading(true);
      try {
        this.vehicleData = await this.loadVehicleData();
        this.populateMakes();
        this.setLoading(false);
        requestAnimationFrame(() => this.makeSelect.focus({ preventScroll: true }));
      } catch (error) {
        this.setLoading(false);
        this.makeSelect.disabled = true;
        this.modelSelect.disabled = true;
        this.submitButton.disabled = true;
        this.error.hidden = false;
        this.status.textContent = '';
      }
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      document.documentElement.classList.remove('search-by-car-modal-open');
      if (this.lastTrigger?.isConnected) this.lastTrigger.focus();
      this.lastTrigger = null;
    }

    loadVehicleData() {
      if (Array.isArray(window.WiperProVehicleData)) {
        return Promise.resolve(window.WiperProVehicleData);
      }
      if (vehicleDataPromise) return vehicleDataPromise;

      vehicleDataPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.dataset.vehicleDataUrl;
        script.async = true;
        script.dataset.searchByCarDataLoader = 'true';
        script.addEventListener('load', () => {
          if (Array.isArray(window.WiperProVehicleData)) {
            resolve(window.WiperProVehicleData);
          } else {
            vehicleDataPromise = undefined;
            reject(new Error('Vehicle data asset did not initialise'));
          }
        });
        script.addEventListener('error', () => {
          vehicleDataPromise = undefined;
          reject(new Error('Vehicle data asset failed to load'));
        });
        document.head.appendChild(script);
      });
      return vehicleDataPromise;
    }

    populateMakes() {
      const makes = [...new Set(this.vehicleData.map((vehicle) => vehicle.make))]
        .sort((a, b) => a.localeCompare(b));
      this.makeSelect.replaceChildren(new Option('Select Make', ''));
      makes.forEach((make) => this.makeSelect.add(new Option(make, make)));
      this.makeSelect.disabled = false;
    }

    updateModels() {
      const make = this.makeSelect.value;
      const models = [...new Set(
        this.vehicleData.filter((vehicle) => vehicle.make === make).map((vehicle) => vehicle.model)
      )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      this.modelSelect.replaceChildren(new Option('Select model', ''));
      models.forEach((model) => this.modelSelect.add(new Option(model, model)));
      this.modelSelect.disabled = models.length === 0;
      this.updateSubmitState();
    }

    updateSubmitState() {
      this.submitButton.disabled = !(this.makeSelect.value && this.modelSelect.value);
    }

    submit() {
      const found = this.vehicleData.find(
        (vehicle) => vehicle.make === this.makeSelect.value && vehicle.model === this.modelSelect.value
      );
      if (!found) {
        this.error.hidden = false;
        this.error.textContent = 'That vehicle could not be found. Please choose another model or contact us.';
        return;
      }
      window.location.assign(`${this.dataset.productsRoot}${encodeURIComponent(found.slug)}`);
    }

    setLoading(loading) {
      this.error.hidden = true;
      if (loading) {
        this.makeSelect.disabled = true;
        this.modelSelect.disabled = true;
        this.submitButton.disabled = true;
        this.status.textContent = 'Loading vehicle options…';
        return;
      }

      this.makeSelect.disabled = false;
      this.modelSelect.disabled = !this.makeSelect.value || this.modelSelect.options.length <= 1;
      this.updateSubmitState();
      this.status.textContent = '';
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-search-by-car-modal]').forEach((root) => {
      if (root.dataset.searchByCarReady === 'true') return;
      root.dataset.searchByCarReady = 'true';
      new SearchByCarModal(root);
    });
  };

  window.WiperProSearchByCar = { hasChooseCarParam, isChooseCarLink, initialise };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
